import { QUERY_KEYS } from '@/config/constant'
import TGASDK from '@/config/TGASDK'
import { Invite } from '@kulee/tga-sdk'
import type { InviteRewardConfig, RechargeRebateConfig } from '@kulee/tga-sdk/types/types/invite'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import useRuntime from './useRuntime'

const CURRENCY_DIAMOND = 'DIAMOND'
const CURRENCY_GOLD = 'GOLD'

const useInviteRewardConfig = () => {
  const { lang, channel } = useRuntime()

  useEffect(() => {
    TGASDK.addModule(Invite)
  }, [])

  return useQuery({
    queryKey: [QUERY_KEYS.INVITE_REWARD_CONFIG, lang, channel],
    queryFn: async (): Promise<UseInviteRewardConfigResult> => {
      const [{ data: inviteRewardConfigList }, { data: rechargeRebateConfigList }] = await Promise.all([
        TGASDK.module.Invite.service.getInviteRewardConfigList(),
        TGASDK.module.Invite.service.getRechargeRebateConfigList()
      ])

      let diamondReward = false
      let goldReward = false
      let bestRebateRate = 0
      let bestDiamondRewardAmount = 0
      let bestGoldRewardAmount = 0
      let rules: Rule[] = [
        {
          key: '1'
        }
      ]

      // 处理邀请奖励配置
      inviteRewardConfigList.forEach(config => {
        config.rewardContents.forEach(content => {
          if (content.currencyCode === CURRENCY_DIAMOND) {
            diamondReward = true
            if (content.count > bestDiamondRewardAmount) {
              bestDiamondRewardAmount = content.count
            }
          }
          if (content.currencyCode === CURRENCY_GOLD) {
            goldReward = true
            if (content.count > bestGoldRewardAmount) {
              bestGoldRewardAmount = content.count
            }
          }
        })
      })

      // 处理充值返利配置
      rechargeRebateConfigList.forEach(config => {
        config.rewardContents.forEach(content => {
          if (content.currencyCode === CURRENCY_DIAMOND) {
            diamondReward = true
            if (content.maxAmount > bestDiamondRewardAmount) {
              bestDiamondRewardAmount = content.maxAmount
            }
          }
          if (content.currencyCode === CURRENCY_GOLD) {
            goldReward = true
            if (content.maxAmount > bestGoldRewardAmount) {
              bestGoldRewardAmount = content.maxAmount
            }
          }
          if (content.ratio > bestRebateRate) {
            bestRebateRate = content.ratio
          }
        })
      })

      if (bestRebateRate >= 0) {
        rules.push({
          key: '2.1',
          data: {
            minRechargeAmount: rechargeRebateConfigList[0].minRechargeAmount,
            bestRebateRate: `${bestRebateRate * 100}%`
          }
        })
        rules.push({
          key: '2.2'
        })
      }

      if (bestDiamondRewardAmount > 0) {
        rules.push({
          key: '2.3',
          data: {
            bestDiamondRewardAmount
          }
        })
      }

      if (bestGoldRewardAmount > 0) {
        rules.push({
          key: '2.4',
          data: {
            bestGoldRewardAmount
          }
        })
      }

      return {
        diamondReward,
        goldReward,
        bestRebateRate: `${bestRebateRate * 100}%`,
        bestDiamondRewardAmount,
        bestGoldRewardAmount,
        rules,
        inviteRewardConfigList,
        rechargeRebateConfigList
      }
    }
  })
}

interface UseInviteRewardConfigResult {
  diamondReward: boolean
  goldReward: boolean
  bestRebateRate: string
  bestDiamondRewardAmount: number
  bestGoldRewardAmount: number
  rules: Rule[]
  inviteRewardConfigList: InviteRewardConfig[]
  rechargeRebateConfigList: RechargeRebateConfig[]
}

interface Rule {
  key: string
  data?: Record<string, any>
}

export default useInviteRewardConfig
